/**
 * Offline-first Supply Request Service
 *
 * Handles caching, creating, and syncing supply requests when offline.
 * Uses IndexedDB for local storage and syncs to the server when online.
 *
 * Two tables are used:
 * - cachedSupplyRequests: mirrors server data for offline viewing
 * - offlineSupplyRequests: locally-created requests pending sync
 * - cachedResourceTypes: resource type/category list for offline dropdown population
 */

import { db, CachedSupplyRequest, OfflineSupplyRequest, CachedResourceType } from './db';
import { api } from '@/services/api';
import { networkService } from '@/services/network.service';
import { useTenantStore } from '@/store/tenant.store';
import { clientIdFor, isDeletedReplay, newClientId, originatorStamp, mustDeferSend, sendTenantOf } from './client-id';
import { resolveSyncableIncidentId } from './incident-offline.service';

/**
 * Read the currently-active tenant id (or null if none / SSR).
 */
function getActiveTenantId(): string | null {
  try {
    return useTenantStore.getState().activeTenant?.id ?? null;
  } catch {
    return null;
  }
}


export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

class OfflineSupplyRequestService {
  // ──────────────────────────────────────────────────────────────────────────
  // Resource Type Caching (for offline dropdown population)
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Cache resource types for offline dropdown population
   */
  async cacheResourceTypes(types: CachedResourceType[]): Promise<void> {
    await db.cachedResourceTypes.clear();
    await db.cachedResourceTypes.bulkPut(types);
  }

  /**
   * Get cached resource types for offline dropdowns
   */
  async getCachedResourceTypes(): Promise<CachedResourceType[]> {
    return db.cachedResourceTypes.toArray();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Offline Supply Request Creation (offlineSupplyRequests table)
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Create a supply request in offline storage
   */
  async create(request: Omit<OfflineSupplyRequest, 'localId' | 'is_synced' | 'status' | 'created_at' | 'updated_at'>): Promise<OfflineSupplyRequest> {
    // P2-05: client id (sent as the server request's id) + originator stamps.
    const stamp = originatorStamp(request.tenant_id);
    const now = stamp.occurred_at;

    const entry: OfflineSupplyRequest = {
      ...request,
      ...stamp,
      is_synced: 0,
      status: 'draft',
      created_at: now,
      updated_at: now,
    };

    const localId = await db.offlineSupplyRequests.add(entry);
    const saved = await db.offlineSupplyRequests.get(localId);
    return saved!;
  }

  /**
   * Get all offline supply requests, optionally filtered by incident
   */
  async getAll(incidentId?: string): Promise<OfflineSupplyRequest[]> {
    if (incidentId) {
      return db.offlineSupplyRequests
        .where('incident_id')
        .equals(incidentId)
        .toArray();
    }
    return db.offlineSupplyRequests.toArray();
  }

  /**
   * Get a single offline supply request by localId
   */
  async getById(localId: number): Promise<OfflineSupplyRequest | undefined> {
    return db.offlineSupplyRequests.get(localId);
  }

  /**
   * Update an offline supply request by localId
   */
  async update(localId: number, updates: Partial<OfflineSupplyRequest>): Promise<OfflineSupplyRequest | null> {
    const existing = await db.offlineSupplyRequests.get(localId);
    if (!existing) return null;

    const updated: OfflineSupplyRequest = {
      ...existing,
      ...updates,
      updated_at: new Date().toISOString(),
      is_synced: 0, // Mark as needing sync after edit
    };

    await db.offlineSupplyRequests.put(updated);
    return updated;
  }

  /**
   * Get count of unsynced offline supply requests
   */
  async getUnsyncedCount(): Promise<number> {
    return db.offlineSupplyRequests
      .where('is_synced')
      .equals(0)
      .count();
  }

  /**
   * Sync all offline supply requests to the server.
   * Creates each request on the server, then auto-submits it.
   */
  async syncOfflineRequests(): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('No network connection');
      return result;
    }

    const pending = await db.offlineSupplyRequests
      .where('is_synced')
      .equals(0)
      .toArray();

    const activeTenantId = getActiveTenantId();

    for (const request of pending) {
      // Tenant guard: never replay a request against a different tenant than the
      // one it was created under (the api client injects the ambient X-Tenant-ID).
      // Defer (leave unsynced) — it will sync when its own tenant is active again.
      if (request.tenant_id != null && activeTenantId != null && request.tenant_id !== activeTenantId) {
        continue;
      }
      // P2-05: another user's request stays queued for its originator.
      if (mustDeferSend(request)) continue;
      // Waiting for its offline-created incident to sync: leave pending.
      const resolved = await resolveSyncableIncidentId(request.incident_id);
      if (resolved.defer) continue;
      try {
        // P2-05: a request queued by an older build has no client id. Give it
        // one and persist it BEFORE the first send, so every retry from now on
        // carries the same id and cannot create a second request.
        let clientId = request.client_id;
        if (!clientId) {
          clientId = newClientId();
          await db.offlineSupplyRequests.update(request.localId!, { client_id: clientId });
        }

        // Map offline items to server format
        const serverItems = (request.items || []).map(item => ({
          item_name: item.name,
          quantity_requested: item.quantity,
          unit_of_measure: item.unit,
          item_description: item.notes || undefined,
        }));

        // Create on server (a replay of the same client id returns the existing request)
        let response: { id: string };
        try {
          response = await api.post<{ id: string }>('/supply-requests', {
          id: clientId,
          incident_id: resolved.incidentId ?? request.incident_id,
          title: request.title,
          description: request.description,
          priority: request.priority,
          needed_by: request.needed_by,
          requesting_unit: request.requesting_unit,
          delivery_location: request.location,
          delivery_address: request.delivery_address,
          delivery_coordinates: request.delivery_coordinates,
          delivery_instructions: request.delivery_instructions,
          items: serverItems,
          }, sendTenantOf(request));
        } catch (createError) {
          if (isDeletedReplay(createError)) {
            // The create landed earlier and the request was deleted since: done.
            await db.offlineSupplyRequests.update(request.localId!, {
              id: clientId,
              is_synced: 1,
              updated_at: new Date().toISOString(),
            });
            result.synced++;
            continue;
          }
          throw createError;
        }

        const serverId = response.id || (response as any).data?.id;
        if (!serverId) {
          throw new Error('No server ID returned');
        }

        // Auto-submit the request
        try {
          await api.post(`/supply-requests/${serverId}/submit`, undefined, sendTenantOf(request));
        } catch {
          // Submit failed but creation succeeded; acceptable
          console.warn(`[SupplyRequest] Created ${serverId} but auto-submit failed`);
        }

        // Mark as synced with server ID
        await db.offlineSupplyRequests.update(request.localId!, {
          id: serverId,
          is_synced: 1,
          status: 'submitted',
          updated_at: new Date().toISOString(),
        });

        result.synced++;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Sync failed';
        console.error(`[SupplyRequest] Offline sync failed for localId=${request.localId}:`, error);
        result.failed++;
        result.errors.push(`Failed to sync "${request.title}": ${message}`);
      }
    }

    result.success = result.failed === 0;
    return result;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Existing cachedSupplyRequests methods (server-mirrored cache)
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Cache supply requests from the server for offline access
   */
  async cacheFromServer(requests: any[]): Promise<number> {
    let cached = 0;

    for (const request of requests) {
      const existing = await db.cachedSupplyRequests.get(request.id);

      if (!existing) {
        await db.cachedSupplyRequests.put({
          id: request.id,
          incident_id: request.incident_id || '',
          data: request,
          status: request.status,
          priority: request.priority,
          is_synced: true,
          synced_at: new Date().toISOString(),
          updated_at: request.updated_at || new Date().toISOString(),
        });
        cached++;
      } else if (existing.is_synced && new Date(request.updated_at) > new Date(existing.updated_at)) {
        await db.cachedSupplyRequests.put({
          id: request.id,
          incident_id: request.incident_id || '',
          data: request,
          status: request.status,
          priority: request.priority,
          is_synced: true,
          synced_at: new Date().toISOString(),
          updated_at: request.updated_at,
        });
        cached++;
      }
    }

    return cached;
  }

  /**
   * Get all cached supply requests, optionally filtered by incident
   */
  async getCached(incidentId?: string): Promise<CachedSupplyRequest[]> {
    if (incidentId) {
      return db.cachedSupplyRequests
        .where('incident_id')
        .equals(incidentId)
        .toArray();
    }

    return db.cachedSupplyRequests.toArray();
  }

  /**
   * Get a single cached supply request by ID
   */
  async getCachedById(id: string): Promise<CachedSupplyRequest | undefined> {
    return db.cachedSupplyRequests.get(id);
  }

  /**
   * Create a supply request offline using the cachedSupplyRequests table
   * (legacy method used by FieldSupplyRequestForm)
   */
  async createOffline(data: any): Promise<CachedSupplyRequest> {
    // P2-05: client id + originator stamps (the client id is also the UUID
    // inside the offline id, so legacy rows replay idempotently as well).
    const stamp = originatorStamp(getActiveTenantId());
    const offlineId = `offline_${stamp.client_id}`;
    const now = stamp.occurred_at;

    const cached: CachedSupplyRequest = {
      ...stamp,
      id: offlineId,
      incident_id: data.incident_id || '',
      data: {
        ...data,
        id: offlineId,
        status: 'draft',
        created_at: now,
        updated_at: now,
      },
      status: 'draft',
      priority: data.priority || 'medium',
      is_synced: false,
      updated_at: now,
    };

    await db.cachedSupplyRequests.add(cached);

    // Try to sync immediately if online
    if (networkService.isOnline()) {
      await this.syncOneCached(offlineId);
    }

    return cached;
  }

  /**
   * Sync a single cached supply request to the server
   */
  private async syncOneCached(id: string): Promise<boolean> {
    const cached = await db.cachedSupplyRequests.get(id);
    if (!cached || cached.is_synced) return true;
    if (mustDeferSend(cached)) return false;

    try {
      const requestData = cached.data;
      const resolved = await resolveSyncableIncidentId(requestData.incident_id ?? cached.incident_id);
      if (resolved.defer) return false;

      let response: any;
      try {
        response = await api.post<any>('/supply-requests', {
        // P2-05: idempotent create; a replay returns the existing request.
        id: clientIdFor({ ...cached, offline_id: cached.id }),
        incident_id: resolved.incidentId ?? undefined,
        title: requestData.title,
        description: requestData.description,
        priority: requestData.priority,
        needed_by: requestData.needed_by,
        requesting_unit: requestData.requesting_unit,
        delivery_location: requestData.delivery_location,
        delivery_address: requestData.delivery_address,
        delivery_coordinates: requestData.delivery_coordinates,
        delivery_instructions: requestData.delivery_instructions,
        estimated_cost: requestData.estimated_cost,
        cost_code: requestData.cost_code,
        tags: requestData.tags,
        items: requestData.items || [],
        }, sendTenantOf(cached));
      } catch (createError) {
        if (isDeletedReplay(createError)) {
          // The create landed earlier and the request was deleted since: done.
          await db.cachedSupplyRequests.delete(id);
          return true;
        }
        throw createError;
      }

      const serverId = response.id || response.data?.id;
      if (!serverId) {
        throw new Error('No server ID returned');
      }

      // Atomic delete-then-put to prevent data loss if put fails
      await db.transaction('rw', db.cachedSupplyRequests, async () => {
        await db.cachedSupplyRequests.delete(id);
        await db.cachedSupplyRequests.put({
          id: serverId,
          incident_id: resolved.incidentId ?? cached.incident_id,
          data: { ...cached.data, id: serverId, incident_id: resolved.incidentId ?? cached.data?.incident_id },
          status: cached.status,
          priority: cached.priority,
          is_synced: true,
          synced_at: new Date().toISOString(),
          updated_at: cached.updated_at,
        });
      });

      return true;
    } catch (error) {
      console.error('[SupplyRequest] Sync failed:', error);
      return false;
    }
  }

  /**
   * Sync all unsynced supply requests to the server.
   * Syncs both cachedSupplyRequests and offlineSupplyRequests tables.
   */
  async syncAll(): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('No network connection');
      return result;
    }

    // Sync cached supply requests (legacy table)
    const pendingCached = await db.cachedSupplyRequests
      .filter(r => !r.is_synced)
      .toArray();

    const activeTenantId = getActiveTenantId();

    for (const request of pendingCached) {
      // Tenant guard (defensive): the legacy CachedSupplyRequest has no
      // tenant_id today, so this never defers — but if a tenant_id is ever
      // stamped on these rows, don't replay them against another tenant.
      const recordTenantId = (request as { tenant_id?: string | null }).tenant_id;
      if (recordTenantId != null && activeTenantId != null && recordTenantId !== activeTenantId) {
        continue;
      }
      // P2-05: another user's request, or one waiting for its offline incident.
      if (mustDeferSend(request)) continue;
      if ((await resolveSyncableIncidentId(request.data?.incident_id ?? request.incident_id)).defer) continue;
      const success = await this.syncOneCached(request.id);
      if (success) {
        result.synced++;
      } else {
        result.failed++;
        result.errors.push(`Failed to sync supply request ${request.id}`);
      }
    }

    // Sync offline supply requests (new table)
    const offlineResult = await this.syncOfflineRequests();
    result.synced += offlineResult.synced;
    result.failed += offlineResult.failed;
    result.errors.push(...offlineResult.errors);

    result.success = result.failed === 0;
    return result;
  }

  /**
   * Clear all cached supply request data
   */
  async clearCache(): Promise<void> {
    await db.cachedSupplyRequests.clear();
    await db.offlineSupplyRequests.clear();
    await db.cachedResourceTypes.clear();
  }
}

export const offlineSupplyRequestService = new OfflineSupplyRequestService();
